import React, { useEffect, useRef } from 'react'

import './CogitationOrb.scss'

// ─── easing ──────────────────────────────────────────────────────────────────
function clamp01(x) {
  return Math.max(0, Math.min(1, x))
}
function norm(t, s, e) {
  return clamp01((t - s) / (e - s))
}
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}
function easeOut3(t) {
  return 1 - (1 - t) ** 3
}
function easeIn3(t) {
  return t ** 3
}
// Overshoots past target then springs back — makes the 45° rotation direction unmistakable
function easeOutBack(t, k = 1.8) {
  const c = k + 1
  return 1 + c * (t - 1) ** 3 + k * (t - 1) ** 2
}
function lerp(a, b, t) {
  return a + (b - a) * t
}

const GRAD_COLORS =
  'oklch(92% 0.04 5), oklch(84% 0.12 342), oklch(76% 0.19 318), oklch(68% 0.23 300), oklch(56% 0.27 282), oklch(44% 0.25 265), oklch(36% 0.19 250)'
const CYCLE_MS = 4000

// ─── OrbAnimator ─────────────────────────────────────────────────────────────
// 1rem circle → 4 cells spread into 2×2 squares → rotate 45° into ◆ →
// each square morphs to a circle clockwise → all collapse back to 1rem circle.
class OrbAnimator {
  constructor(stageEl, cellSize, gap) {
    this.cellSize = cellSize
    this.gradSpan = cellSize * 2 + gap

    // Full-size resting circle — rendered at actual gradSpan dimensions so it's crisp.
    // Lives in stageEl (not the rotating anchor) so it doesn't spin with the diamond.
    const restEl = document.createElement('div')
    restEl.style.cssText = `
      position: absolute; left: 0; top: 0;
      width: ${this.gradSpan}px; height: ${this.gradSpan}px;
      border-radius: 50%;
      background-size: ${this.gradSpan}px ${this.gradSpan}px;
      background-repeat: no-repeat;
      background-position: 0 0;
      will-change: opacity;
    `
    stageEl.appendChild(restEl)
    this.restingCircle = restEl

    this.anchor = document.createElement('div')
    this.anchor.style.cssText = `
      position: absolute; left: 50%; top: 50%;
      width: 0; height: 0; transform-origin: 0 0;
      will-change: transform;
    `
    stageEl.appendChild(this.anchor)

    const half = (cellSize + gap) / 2

    this.cells = Array.from({ length: 4 }, () => {
      const el = document.createElement('div')
      el.className = 'cogitation-orb__cell'
      el.style.width = `${cellSize}px`
      el.style.height = `${cellSize}px`
      el.style.left = `${-cellSize / 2}px`
      el.style.top = `${-cellSize / 2}px`
      el.style.backgroundSize = `${this.gradSpan}px ${this.gradSpan}px`
      el.style.backgroundRepeat = 'no-repeat'
      this.anchor.appendChild(el)
      return el
    })

    // 2×2 grid: TL, TR, BL, BR — relative to center anchor
    this.targets = [
      { x: -half, y: -half }, // 0: TL → top of diamond
      { x: half, y: -half }, // 1: TR → right
      { x: -half, y: half }, // 2: BL → left
      { x: half, y: half }, // 3: BR → bottom
    ]

    // Clockwise circleize order after 45° CW rotation: top→right→bottom→left
    const circleOrder = [0, 1, 3, 2]
    const cWindowStart = 0.34
    const cWindow = 0.04
    const cGap = 0.006

    this.circleWindows = {}
    circleOrder.forEach((cellIdx, order) => {
      const s = cWindowStart + order * (cWindow + cGap)
      this.circleWindows[cellIdx] = { start: s, end: s + cWindow }
    })

    // Each cell dims when the *next* cell starts its circleize, not when it finishes its own.
    this.shrinkTriggers = {}
    circleOrder.forEach((cellIdx, order) => {
      const nextIdx = circleOrder[order + 1]
      this.shrinkTriggers[cellIdx] =
        nextIdx != null ? this.circleWindows[nextIdx].start : this.circleWindows[cellIdx].end
    })

    this._lastGradAngle = null
  }

  render(t, gradAngle) {
    // ── Timing windows ──────────────────────────────────────────
    //  0.00 – 0.04  rest
    //  0.04 – 0.11  spread outward + squarify  (fast)
    //  0.11 – 0.27  rotate 45° + overshoot
    //  0.27 – 0.34  hold diamond
    //  0.34 – ~0.52 circleize one by one
    //  0.60 – 0.68  collapse back → resting circle fades in
    //  0.68 – 1.00  rest

    const spreadP = easeOut3(norm(t, 0.04, 0.11))
    const squareP = easeInOut(norm(t, 0.04, 0.11))
    const rotateRaw = norm(t, 0.11, 0.27)
    const rotateP = easeOutBack(rotateRaw, 1.7)
    const collapseP = easeIn3(norm(t, 0.6, 0.68))

    // 1 at rest/after-collapse, 0 when fully spread — drives the resting circle opacity
    const bigP = 1 - spreadP * (1 - collapseP)

    // Only rebuild the gradient string when the angle has shifted by ≥1°
    const roundedAngle = Math.round(gradAngle)
    const gradChanged = roundedAngle !== this._lastGradAngle
    if (gradChanged) {
      this._lastGradAngle = roundedAngle
      this.restingCircle.style.backgroundImage = `linear-gradient(in oklch ${roundedAngle}deg, ${GRAD_COLORS})`
    }

    this.restingCircle.style.opacity = bigP

    this.cells.forEach((cell, i) => {
      const tgt = this.targets[i]
      const tx = tgt.x * (spreadP - collapseP)
      const ty = tgt.y * (spreadP - collapseP)

      const cw = this.circleWindows[i]
      const circleP = easeInOut(norm(t, cw.start, cw.end))
      const radius = circleP > 0 ? lerp(0, 50, circleP) : lerp(50, 0, squareP)

      const popT = norm(t, cw.start, cw.end)
      const shrinkAt = this.shrinkTriggers[i]
      const shrinkP = easeInOut(norm(t, shrinkAt, shrinkAt + 0.012))
      const cellScale = t <= shrinkAt ? 1 + 0.25 * Math.sin(Math.PI * popT) : lerp(1, 0.75, shrinkP)

      const opacity = Math.min(spreadP, 1 - collapseP) * (1 - shrinkP * 0.6)

      const bpx = -(tx - this.cellSize / 2 + this.gradSpan / 2)
      const bpy = -(ty - this.cellSize / 2 + this.gradSpan / 2)

      if (gradChanged) {
        cell.style.backgroundImage = `linear-gradient(in oklch ${roundedAngle}deg, ${GRAD_COLORS})`
      }
      cell.style.transform = `translate(${tx}px, ${ty}px) scale(${cellScale})`
      cell.style.borderRadius = `${radius}%`
      cell.style.opacity = opacity
      cell.style.backgroundPosition = `${bpx}px ${bpy}px`
    })

    this.anchor.style.transform = `rotate(${rotateP * 45}deg)`
  }

  drawRest() {
    this.restingCircle.style.backgroundImage = `linear-gradient(in oklch 135deg, ${GRAD_COLORS})`
    this.restingCircle.style.opacity = 1
    this.cells.forEach((cell) => {
      cell.style.opacity = 0
    })
    this.anchor.style.transform = 'rotate(0deg)'
  }
}

// ─── Component ───────────────────────────────────────────────────────────────
/**
 * @import import { CogitationOrb } from '@/components/ai/CogitationOrb'
 * @param {object} props
 * @param {boolean} [props.resting] Shows the static gradient circle instead of looping the animation
 */
export function CogitationOrb({ resting = false }) {
  const stageRef = useRef(null)

  useEffect(() => {
    const stageEl = stageRef.current
    if (!stageEl) {
      return () => {}
    }

    const orb = new OrbAnimator(stageEl, 5, 4)

    if (resting || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      orb.drawRest()
      return () => {
        orb.restingCircle.remove()
        orb.anchor.remove()
      }
    }

    // Stagger start so multiple instances on the same page stay out of phase
    const offset = Math.random() * CYCLE_MS
    let startTime = null
    let rafId

    function loop(now) {
      if (startTime === null) {
        startTime = now
      }
      const rawElapsed = now - startTime + offset
      const t = (rawElapsed % CYCLE_MS) / CYCLE_MS
      const gradAngle = 135 + (rawElapsed / 1500) * 360
      orb.render(t, gradAngle)
      rafId = requestAnimationFrame(loop)
    }

    rafId = requestAnimationFrame(loop)
    return () => {
      cancelAnimationFrame(rafId)
      orb.restingCircle.remove()
      orb.anchor.remove()
    }
  }, [resting])

  return <div ref={stageRef} className="cogitation-orb" />
}
